/**
 * Rate Limit Utilities
 * Handles HTTP 429 (Too Many Requests) errors with user-friendly messages
 */

/**
 * Get user-friendly error message for rate limited endpoint
 * @param {string} endpoint - API endpoint that was rate limited
 * @param {number} retryAfter - Seconds until retry is allowed
 * @returns {Object} - Object with message and retryAfter
 */
export const getRateLimitMessage = (endpoint, retryAfter = 900) => {
  const messages = {
    '/oauthtoken': {
      message: "Too many login attempts. Please wait 15 minutes before trying again.",
      defaultRetryAfter: 900, // 15 minutes
    },
    '/send-otp': {
      message: "Too many password reset requests. Please wait 1 hour before requesting another OTP.",
      defaultRetryAfter: 3600, // 1 hour
    },
    '/resend-otp': {
      message: "Too many OTP resend requests. Please wait 15 minutes before trying again.",
      defaultRetryAfter: 900, // 15 minutes
    },
    '/verify-otp': {
      message: "Too many OTP verification attempts. Please wait 15 minutes or request a new OTP.",
      defaultRetryAfter: 900, // 15 minutes
    },
    '/token/refresh': {
      message: "Too many token refresh requests. Please wait 1 minute before trying again.",
      defaultRetryAfter: 60, // 1 minute
    },
  };

  const endpointMessage = messages[endpoint] || {
    message: `Too many requests. Please try again in ${Math.ceil(retryAfter / 60)} minute${Math.ceil(retryAfter / 60) > 1 ? 's' : ''}.`,
    defaultRetryAfter: retryAfter,
  };

  return {
    message: endpointMessage.message,
    retryAfter: retryAfter || endpointMessage.defaultRetryAfter,
  };
};

/**
 * Extract retry-after header from error response
 * @param {Object} error - Axios error object
 * @returns {number} - Seconds until retry (default 900)
 */
export const extractRetryAfter = (error) => {
  if (!error?.response?.headers) return 900;
  
  const retryAfter = error.response.headers['retry-after'] || 
                     error.response.headers['Retry-After'] ||
                     error.response.headers['X-RateLimit-Reset'];
  
  if (retryAfter) {
    // Handle both Unix timestamp and seconds
    const timestamp = parseInt(retryAfter, 10);
    const now = Math.floor(Date.now() / 1000);
    
    // If timestamp is in the future (Unix timestamp), calculate difference
    if (timestamp > now) {
      return timestamp - now;
    }
    
    // Otherwise assume it's already in seconds
    return timestamp;
  }
  
  return 900; // Default 15 minutes
};

/**
 * Extract endpoint from error config
 * @param {Object} error - Axios error object
 * @returns {string} - Endpoint path
 */
export const extractEndpoint = (error) => {
  if (!error?.config?.url) return '';
  
  try {
    const url = new URL(error.config.url);
    return url.pathname;
  } catch {
    // If URL parsing fails, try to extract path from url string
    const urlString = error.config.url;
    const match = urlString.match(/https?:\/\/[^/]+(\/[^?#]*)/);
    return match ? match[1] : '';
  }
};

/**
 * Check if error is a rate limit error (HTTP 429)
 * @param {Object} error - Error object
 * @returns {boolean}
 */
export const isRateLimitError = (error) => {
  return error?.response?.status === 429;
};

